/**
 * The reimbursement package: the one document a client gets. Cover with the
 * category totals, the itemized list, then every receipt grouped by category.
 *
 * Printed through the browser like the invoice attachment — Print, then "Save
 * as PDF" — so what is on screen is what goes out. The phone app builds a PDF
 * itself only because an installed iOS web app has no print option; a desk
 * browser does not need that.
 *
 * PDF receipts are rendered to page images first, since a print dialog skips
 * an embedded PDF; Print waits for them, and for every image to finish loading,
 * or the paper comes out with blanks where receipts should be.
 */

import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";

import { receiptUrl } from "@/hooks/useExpenses";
import {
  categoryNamer, fmtDay, fmtPeriod, isPdf, money, packageGroups, todayIso, type Totals,
} from "@/lib/expenses";
import { pdfPages } from "@/lib/pdfPages";
import {
  ReportFooter, ReportMasthead, reportH2, reportLabel, reportPaper, reportRight, reportTable, reportTd, reportTh, reportTotalTd,
} from "@/components/ReportMasthead";
import type { Client, Expense, ExpenseCategory, ExpenseReport, Project } from "@/lib/types";

import { ClientNotes, ghostBtn, primaryBtn } from "./ui";

interface Shot { key: string; cat: string; e: Expense; name: string; url: string; pdf: boolean }

export function Package({ report, client, project, totals, cats, onClose }: {
  report: ExpenseReport;
  client?: Client;
  project?: Project;
  totals: Totals;
  cats: ExpenseCategory[];
  onClose: () => void;
}) {
  const catName = useMemo(() => categoryNamer(cats), [cats]);
  const groups = useMemo(() => packageGroups(totals.items, cats), [totals.items, cats]);
  const items = totals.reimbursable;

  const shots: Shot[] = useMemo(() => groups.flatMap(([cat, list]) => list.flatMap((e) =>
    (e.receipts ?? []).map((name) => ({ key: `${e.id}/${name}`, cat, e, name, url: receiptUrl(e, name), pdf: isPdf(name) })))), [groups]);
  const noReceipt = items.filter((e) => !e.is_mileage && !(e.receipts ?? []).length);

  // PDF receipts → page images. `null` while rendering, [] if it failed.
  const [pages, setPages] = useState<Record<string, string[] | null>>({});
  useEffect(() => {
    let live = true;
    const pdfs = shots.filter((s) => s.pdf);
    setPages(Object.fromEntries(pdfs.map((s) => [s.key, null])));
    for (const s of pdfs) {
      pdfPages(s.url)
        .then((p) => { if (live) setPages((m) => ({ ...m, [s.key]: p })); })
        .catch(() => { if (live) setPages((m) => ({ ...m, [s.key]: [] })); });
    }
    return () => { live = false; };
  }, [shots]);
  const rendering = Object.values(pages).filter((p) => p === null).length;

  const [printing, setPrinting] = useState(false);
  async function print() {
    setPrinting(true);
    try {
      const imgs = Array.from(document.querySelectorAll<HTMLImageElement>(".print-sheet img"))
        .filter((im) => !im.complete || !im.naturalWidth);
      await Promise.all(imgs.map((im) => new Promise<void>((done) => {
        im.addEventListener("load", () => done(), { once: true });
        im.addEventListener("error", () => done(), { once: true });
        setTimeout(done, 8000); // never hold printing hostage to one bad file
      })));
      window.print();
    } finally {
      setPrinting(false);
    }
  }

  // The browser offers this as the PDF's file name.
  useEffect(() => {
    const was = document.title;
    document.title = `${report.name} — expense reimbursement`;
    return () => { document.title = was; };
  }, [report.name]);


  // Rendered straight into <body>, outside the app's layout. Inside it, the
  // report page (position: relative, for its drop outline) became the sheet's
  // containing block, and the app shell's fixed-height, overflow-hidden panes
  // clipped the printout to a single page — the screen showed everything,
  // the PDF only what was in view.
  return createPortal(
    <div className="print-sheet" style={{ position: "fixed", inset: 0, zIndex: 70, overflowY: "auto", background: "var(--bg)" }}>
      <div className="no-print" style={{
        position: "sticky", top: 0, zIndex: 1, background: "var(--surface)", borderBottom: "1px solid var(--line)",
        padding: "14px 24px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
      }}>
        <span style={{ fontSize: 16, fontWeight: "var(--fw-title)" }}>Reimbursement package</span>
        <span style={{ fontSize: 12, color: "var(--muted)" }}>
          {items.length} item{items.length === 1 ? "" : "s"} · {money(totals.total)} · {shots.length} receipt{shots.length === 1 ? "" : "s"}
          {rendering > 0 && ` · rendering ${rendering} PDF${rendering === 1 ? "" : "s"}…`}
        </span>
        {totals.held > 0 && (
          <span style={{ fontSize: 12, color: "var(--muted)" }}>
            {totals.held} on hold, not included
          </span>
        )}
        {totals.unpriced > 0 && (
          <span style={{ fontSize: 12, color: "var(--warn)" }}>
            {totals.unpriced} item{totals.unpriced === 1 ? " has" : "s have"} no amount yet
          </span>
        )}
        <span style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button onClick={() => void print()} disabled={rendering > 0 || printing} style={{ ...primaryBtn, opacity: rendering > 0 ? 0.6 : 1 }}>
            {rendering > 0 ? "Preparing receipts…" : "Print / Save PDF"}
          </button>
          <button onClick={onClose} style={ghostBtn}>Close</button>
        </span>
      </div>

      <div className="pkg-paper" style={{ maxWidth: 820, margin: "24px auto 60px", background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 6 }}>
        {/* ---- Cover ---- */}
        <section className="print-page" style={page}>
          <ReportMasthead
            kind="Expense reimbursement"
            title={report.name}
            subtitle={[client?.name, project?.name].filter(Boolean).join(" · ")}
            meta={[
              ["Period", fmtPeriod(report.start_date, report.end_date)],
              ["Invoice", report.invoice_number],
              ["Prepared", fmtDay(todayIso())],
            ]}
          />
          <table style={table}>
            <thead><tr><th style={pth}>Category</th><th style={{ ...pth, ...right }}>Items</th><th style={{ ...pth, ...right }}>Amount</th></tr></thead>
            <tbody>
              {groups.map(([cat, list]) => (
                <tr key={cat}>
                  <td style={ptd}>{cat}</td>
                  <td style={{ ...ptd, ...right }}>{list.length}</td>
                  <td style={{ ...ptd, ...right }} className="mono">{money(list.reduce((s, e) => s + (Number(e.amount) || 0), 0))}</td>
                </tr>
              ))}
              {groups.length === 0 && <tr><td style={ptd} colSpan={3}>No expenses.</td></tr>}
            </tbody>
            <tfoot>
              <tr>
                <td style={reportTotalTd}>Total reimbursable</td>
                <td style={{ ...reportTotalTd, ...right }}>{items.length}</td>
                <td style={{ ...reportTotalTd, ...right, fontSize: 14 }} className="mono">{money(totals.total)}</td>
              </tr>
            </tfoot>
          </table>

          {client?.reimbursementNotes && (
            <div style={{ marginTop: 22, fontSize: 12.5 }}>
              <div style={metaKey}>Submission notes</div>
              <div style={{ whiteSpace: "pre-wrap", color: "var(--ink3)" }}>{client.reimbursementNotes}</div>
            </div>
          )}
          {/* Editing stays on screen; only the note itself prints. */}
          <div className="no-print" style={{ marginTop: 16 }}><ClientNotes client={client} /></div>

          {noReceipt.length > 0 && (
            <p style={{ marginTop: 18, fontSize: 12, color: "var(--muted)" }}>
              {noReceipt.length} item{noReceipt.length === 1 ? "" : "s"} with no receipt image attached:{" "}
              {noReceipt.map((e) => e.description || fmtDay(e.date)).join(", ")}
            </p>
          )}
        </section>

        {/* ---- Itemized ---- */}
        <section className="print-page" style={{ ...page, ...breakBefore }}>
          <h2 style={h2}>Itemized</h2>
          <table style={table}>
            <thead>
              <tr><th style={pth}>Date</th><th style={pth}>Category</th><th style={pth}>Description</th><th style={{ ...pth, ...right }}>Amount</th></tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id} style={{ breakInside: "avoid" }}>
                  <td style={{ ...ptd, whiteSpace: "nowrap" }}>{fmtDay(e.date)}</td>
                  <td style={ptd}>{catName(e.category)}</td>
                  <td style={ptd}>
                    {e.description}
                    {e.is_mileage && (
                      <div style={{ fontSize: 11.5, color: "var(--muted)" }}>
                        {[e.from_location, e.to_location].filter(Boolean).join(" → ")}
                        {(e.from_location || e.to_location) && " · "}
                        {e.miles || 0} mi × {money(e.mileage_rate)}/mi{e.round_trip ? " · round trip" : ""}
                      </div>
                    )}
                    {e.is_mileage && e.notes?.trim() && (
                      <div style={{ fontSize: 11.5, color: "var(--muted)" }}>Purpose: {e.notes.trim()}</div>
                    )}
                  </td>
                  <td style={{ ...ptd, ...right }} className="mono">{money(e.amount)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3} style={{ ...reportTotalTd, ...right }}>Total reimbursable</td>
                <td style={{ ...reportTotalTd, ...right, fontSize: 14 }} className="mono">{money(totals.total)}</td>
              </tr>
            </tfoot>
          </table>
          <ReportFooter />
        </section>

        {/* ---- Receipts: one per page, as the phone's saved PDF lays them out ----
            Each receipt (and each page of a PDF receipt) is its own page with
            its own margins. Flowing them down one section used to push a
            receipt that didn't fit onto the next sheet with no top margin, so
            it printed flush against the edge or clipped. */}
        {shots.flatMap((s) => {
          const caption = `${fmtDay(s.e.date)} · ${s.e.description || s.cat} · ${money(s.e.amount)}`;
          const sheet = (key: string, body: React.ReactNode, cap = caption) => (
            <section key={key} className="print-page" style={{ ...page, ...breakBefore, breakInside: "avoid" }}>
              <div style={receiptHead}>{s.cat} — receipt</div>
              {body}
              <div style={receiptCaption}>{cap}</div>
            </section>
          );
          if (!s.pdf) return [sheet(s.key, <img src={s.url} alt={s.name} style={receiptImg} />)];
          const p = pages[s.key];
          if (p === undefined || p === null) return [sheet(s.key, <div style={placeholder}>{s.name} — rendering…</div>)];
          if (!p.length) return [sheet(s.key, <div style={placeholder}>{s.name} — couldn't render this PDF; attach it separately.</div>)];
          return p.map((src, i) => sheet(`${s.key}#${i}`, <img src={src} alt={s.name} style={receiptImg} />,
            caption + (p.length > 1 ? ` · page ${i + 1} of ${p.length}` : "")));
        })}
        {shots.length === 0 && (
          <section className="print-page" style={{ ...page, ...breakBefore }}>
            <p style={{ fontSize: 12.5, color: "var(--muted)" }}>No receipt images attached to this report yet.</p>
          </section>
        )}
      </div>
    </div>,
    document.body,
  );
}

const page = reportPaper;
const breakBefore: CSSProperties = { breakBefore: "page", borderTop: "1px solid var(--line)" };
const h2 = reportH2;
const metaKey: CSSProperties = { ...reportLabel, marginBottom: 3 };
const table = reportTable;
const pth = reportTh;
const ptd = reportTd;
const right = reportRight;
/* Letter is 279mm tall: the page's padding, the heading and the caption take
   about 50mm of it, so a receipt is capped at 220mm and scaled to fit, never
   cropped. A4 has more room, so the same cap works there. */
const receiptImg: CSSProperties = {
  display: "block", margin: "0 auto", maxWidth: "100%", maxHeight: "220mm", objectFit: "contain",
  border: "1px solid var(--line)", background: "#fff",
};
const receiptHead: CSSProperties = { ...reportLabel, marginBottom: 12 };
const receiptCaption: CSSProperties = { fontSize: 11.5, color: "var(--muted)", marginTop: 10, textAlign: "center" };
const placeholder: CSSProperties = { padding: 18, border: "1px dashed var(--line2)", borderRadius: 6, fontSize: 12, color: "var(--muted)", marginBottom: 16 };
